import { Account } from "@common/types/domains/accounting/account";
import { CellType } from "../spreadsheet/cells";
import { ColumnDef, SpreadsheetCell } from "../spreadsheet/types";
import { AccountMappingSpreadsheetRow } from "./types";
import { isAccountRow, isGroupRow } from "./rowTypeGuards";

const DEPTH_INDENT_PX = 12;

// Type review edits only top-level subjects: a root group propagates its type
// to every account nested below it, so nested rows are read-only.
function isTypeColumnEditable({
    row,
    isTypeReview,
}: {
    row: AccountMappingSpreadsheetRow | undefined;
    isTypeReview: boolean;
}): boolean {
    if (!row) {
        return false;
    }
    if (row._isGroup) {
        return isTypeReview && row._depth === 0;
    }
    if (isTypeReview) {
        return row._depth === 0;
    }
    if (!row.createNewAccount) {
        return false;
    }
    return true;
}

export function makeColumnsForGroup(options: {
    groupRows: AccountMappingSpreadsheetRow[];
    kickAccounts: Account[];
    shimmerClassName: ColumnDef["className"];
    providerLabel?: string;
    hideKickAccountColumn?: boolean;
    isTypeReview?: boolean;
}): ColumnDef[] {
    const {
        groupRows,
        kickAccounts,
        shimmerClassName,
        providerLabel = "Imported",
        hideKickAccountColumn = false,
        isTypeReview = false,
    } = options;

    const dataColumns: ColumnDef[] = [
        {
            field: "name",
            label: `${providerLabel} account`,
            cellType: CellType.STRING,
            isEditable: false,
            cellFn: (props) => {
                const row = props.row;
                if (isGroupRow(row)) {
                    const paddingLeft = row._depth * DEPTH_INDENT_PX;
                    return <div style={{ paddingLeft }}>{row.name}</div>;
                }
                if (isAccountRow(row)) {
                    const paddingLeft = row._depth * DEPTH_INDENT_PX;
                    return <div style={{ paddingLeft }}>{row.name ?? ""}</div>;
                }
                return undefined;
            },
        },
        ...(isTypeReview
            ? [
                  {
                      field: "providerType",
                      label: `${providerLabel} type`,
                      cellType: CellType.STRING,
                      isEditable: false,
                  } satisfies ColumnDef,
              ]
            : []),
        {
            field: "kickAccountId",
            label: "Mapped Kick account",
            cellType: CellType.ACCOUNT_SEARCH,
            accounts: kickAccounts,
            className: shimmerClassName,
            isEditable: (cell: SpreadsheetCell) => {
                const row = groupRows[cell.row];
                return !!row && !row._isGroup;
            },
            cellFn: (props) => {
                if (isGroupRow(props.row)) {
                    return null;
                }
                return undefined;
            },
            toClipboard: (value, row) => {
                if (isGroupRow(row)) {
                    return "";
                }
                if (isAccountRow(row)) {
                    if (!value && row.createNewAccount) {
                        return row.name ?? "";
                    }
                }
                return undefined;
            },
            newAccountInfo: (row: Record<string, unknown>) => {
                if (isGroupRow(row)) {
                    return undefined;
                }
                if (isAccountRow(row)) {
                    return row.createNewAccount
                        ? { name: row.name }
                        : undefined;
                }
                return undefined;
            },
        },
        {
            field: "accountType",
            label: isTypeReview ? "Type" : "Group",
            cellType: CellType.ACCOUNT_TYPE,
            className: shimmerClassName,
            isEditable: (cell: SpreadsheetCell) =>
                isTypeColumnEditable({
                    row: groupRows[cell.row],
                    isTypeReview,
                }),
            cellFn: (props) => {
                if (isGroupRow(props.row) && !isTypeReview) {
                    return null;
                }
                return undefined;
            },
        },
    ];

    return hideKickAccountColumn
        ? dataColumns.filter((column) => column.field !== "kickAccountId")
        : dataColumns;
}
